import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, Fieldset, Label, NumberField } from '@heroui/react';
import { TextContext } from 'react-aria-components/Text';
import { getNodeTestIdProp } from '@/utils';
import {
    PRODUCT_RANGE_ENDS,
    type ProductListRange,
    type ProductRangeFilter,
} from '../productListFilters';
import {
    RANGE_END_LABEL_KEY,
    RANGE_FIELD_FORMAT,
    RANGE_LEGEND_KEY,
    fromRangeFieldValue,
    toRangeFieldValue,
} from './rangeFilterMeta';

type RangeFilterFieldProps = {
    filter: ProductRangeFilter;
    value: ProductListRange;
    /** The minimum is above the maximum: both fields read invalid. */
    isInverted: boolean;
    onChange: (range: ProductListRange) => void;
};

/**
 * One of the filters drawer's ranges (BOS-203): a Min and a Max number field
 * side by side, each labelled inside its box, under the range's name. Either
 * may be left empty. Money is typed in GEL; a field takes its value when it
 * loses focus, as React Aria's number field does.
 */
export const RangeFilterField: React.FC<RangeFilterFieldProps> = ({
    filter,
    value,
    isInverted,
    onChange,
}) => {
    const { t } = useTranslation('pages');
    const errorId = React.useId();

    return (
        <Fieldset
            className="flex-none gap-0"
            {...getNodeTestIdProp(`product-filters-range-${filter}`)}
        >
            <Fieldset.Legend className="mb-2">
                <Label elementType="span">{t(RANGE_LEGEND_KEY[filter])}</Label>
            </Fieldset.Legend>
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    {PRODUCT_RANGE_ENDS.map((end, index) => (
                        <React.Fragment key={end}>
                            {index > 0 ? (
                                <span aria-hidden="true">–</span>
                            ) : null}
                            <NumberField
                                aria-describedby={
                                    isInverted ? errorId : undefined
                                }
                                className="min-w-0 flex-1"
                                formatOptions={RANGE_FIELD_FORMAT[filter]}
                                isInvalid={isInverted}
                                minValue={0}
                                onChange={(next) =>
                                    onChange({
                                        ...value,
                                        [end]: fromRangeFieldValue(
                                            filter,
                                            next,
                                        ),
                                    })
                                }
                                value={toRangeFieldValue(filter, value[end])}
                                {...getNodeTestIdProp(
                                    `product-filters-range-${filter}-${end}`,
                                )}
                            >
                                <NumberField.Group className="grid-cols-[auto_1fr]">
                                    <Label className="ps-3">
                                        {t(RANGE_END_LABEL_KEY[end])}
                                    </Label>
                                    <NumberField.Input
                                        placeholder={t(
                                            'products.components.productTable.filters.rangeAny',
                                        )}
                                    />
                                </NumberField.Group>
                            </NumberField>
                        </React.Fragment>
                    ))}
                </div>
                {isInverted ? (
                    // The message belongs to the pair, so it sits outside
                    // both fields. There, the drawer's dialog still hands its
                    // own text slots down (only `description`), and React Aria
                    // throws on an error message that isn't one of them.
                    <TextContext.Provider value={null}>
                        <ErrorMessage
                            id={errorId}
                            {...getNodeTestIdProp(
                                `product-filters-range-${filter}-error`,
                            )}
                        >
                            {t(
                                'products.components.productTable.filters.rangeInverted',
                            )}
                        </ErrorMessage>
                    </TextContext.Provider>
                ) : null}
            </div>
        </Fieldset>
    );
};
